import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import coverAsset from "@/assets/the-diamond-sutra-cover.png.asset.json";
import {
  getPressRoomState,
  getPressStreamUrl,
  redeemPressCode,
  endPressSession,
  type PressRoomState,
} from "@/lib/press-listening.functions";

export const Route = createFileRoute("/press/the-diamond-sutra/listen")({
  // Private, pre-release, invite-only. Never indexed, never previewed socially,
  // and deliberately absent from the sitemap and on-site search.
  head: () => ({
    meta: [
      { title: "Private Advance Listening — Buddha KA" },
      { name: "robots", content: "noindex, nofollow, noarchive, nosnippet, noimageindex" },
      { name: "googlebot", content: "noindex, nofollow" },
      { name: "referrer", content: "no-referrer" },
    ],
  }),
  component: PressListeningRoom,
});

function PressListeningRoom() {
  const loadState = useServerFn(getPressRoomState);
  const redeem = useServerFn(redeemPressCode);
  const endSession = useServerFn(endPressSession);
  const requestStream = useServerFn(getPressStreamUrl);

  const [state, setState] = useState<PressRoomState | null>(null);
  const [code, setCode] = useState("");
  const [gateError, setGateError] = useState<string | null>(null);
  const [gateBusy, setGateBusy] = useState(false);

  const refresh = useCallback(async () => {
    setState(await loadState());
  }, [loadState]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setGateBusy(true);
    setGateError(null);
    const result = await redeem({ data: { code } });
    setGateBusy(false);
    if ("error" in result) {
      setGateError(result.error);
      return;
    }
    setCode("");
    await refresh();
  }

  if (!state) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-24 text-center text-muted-foreground">
        <p>Opening the listening room…</p>
      </main>
    );
  }

  if (!state.authorized) {
    return (
      <main className="mx-auto max-w-md px-4 py-16">
        <Confidential />
        <h1 className="mt-6 text-2xl font-semibold tracking-tight">Private Advance Listening</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Buddha KA — The Diamond Sutra. Access is limited to accredited press and media reviewers.
        </p>
        <form onSubmit={submitCode} className="mt-8 space-y-3" aria-describedby="press-gate-help">
          <label htmlFor="press-code" className="block text-sm font-medium">
            Press access code
          </label>
          <input
            id="press-code"
            name="press-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            required
            className="min-h-11 w-full rounded-md border border-border bg-background px-3 text-base"
            placeholder="DS-XXXX-XXXX-XXXX"
          />
          <p id="press-gate-help" className="text-xs text-muted-foreground">
            Codes are issued individually per outlet and may expire or be revoked.
          </p>
          {gateError ? (
            <p role="alert" className="text-sm text-destructive">
              {gateError}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={gateBusy}
            className="min-h-11 w-full rounded-md bg-primary px-4 font-medium text-primary-foreground disabled:opacity-60"
          >
            {gateBusy ? "Verifying…" : "Enter listening room"}
          </button>
        </form>
        <PressLinks />
      </main>
    );
  }

  return (
    <ListeningRoom
      state={state}
      requestStream={requestStream}
      onExit={async () => {
        await endSession();
        await refresh();
      }}
    />
  );
}

function Confidential() {
  return (
    <p className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs uppercase tracking-widest text-primary">
      Confidential · Pre-release · Not for distribution
    </p>
  );
}

function PressLinks() {
  return (
    <nav className="mt-10 flex flex-wrap gap-4 text-sm" aria-label="Press resources">
      <Link to="/press/the-diamond-sutra" className="min-h-11 items-center underline underline-offset-4">
        Official EPK
      </Link>
      <Link to="/artist/buddha-ka" className="min-h-11 items-center underline underline-offset-4">
        Artist page
      </Link>
    </nav>
  );
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function ListeningRoom({
  state,
  requestStream,
  onExit,
}: {
  state: PressRoomState;
  requestStream: (args: { data: { songSlug: string } }) => Promise<
    { url: string; expiresInSeconds: number } | { error: string }
  >;
  onExit: () => void | Promise<void>;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);

  const tracks = state.tracks;
  const active = activeIndex == null ? null : tracks[activeIndex];

  const playable = useMemo(() => tracks.filter((t) => t.available).length, [tracks]);

  const play = useCallback(
    async (index: number) => {
      const track = tracks[index];
      if (!track || !track.available) {
        setError("That track has not been loaded into the listening room yet.");
        return;
      }
      setError(null);
      setLoadingIndex(index);
      // A fresh signed URL is minted per play; nothing long-lived is cached.
      const result = await requestStream({ data: { songSlug: track.slug } });
      setLoadingIndex(null);
      if ("error" in result) {
        setError(result.error);
        return;
      }
      const el = audioRef.current;
      if (!el) return;
      el.src = result.url;
      setActiveIndex(index);
      try {
        await el.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    },
    [requestStream, tracks],
  );

  function toggle(index: number) {
    const el = audioRef.current;
    if (activeIndex === index && el && !el.paused) {
      el.pause();
      setPlaying(false);
      return;
    }
    if (activeIndex === index && el && el.src) {
      void el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      return;
    }
    void play(index);
  }

  function step(delta: number) {
    if (activeIndex == null) return;
    let next = activeIndex + delta;
    while (next >= 0 && next < tracks.length && !tracks[next]!.available) next += delta;
    if (next < 0 || next >= tracks.length) return;
    void play(next);
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <Confidential />

      <header className="mt-6 flex flex-col gap-5 sm:flex-row sm:items-end">
        <img
          src={coverAsset.url}
          alt="The Diamond Sutra album cover"
          width={160}
          height={160}
          className="w-32 rounded-lg border border-border sm:w-40"
        />
        <div>
          <p className="text-sm uppercase tracking-widest text-muted-foreground">Buddha KA</p>
          <h1 className="text-3xl font-semibold tracking-tight">The Diamond Sutra</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Private Advance Listening — For Press / Media Review
          </p>
          <p className="text-sm text-muted-foreground">August 19, 2026 · 17 tracks</p>
          {state.outlet ? (
            <p className="mt-2 text-xs text-muted-foreground">Session issued to: {state.outlet}</p>
          ) : null}
        </div>
      </header>

      {!state.ready ? (
        <p role="status" className="mt-6 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
          This room is still being prepared — {state.availableCount} of {state.totalCount} masters are
          loaded. Tracks without audio cannot be played.
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="mt-6 rounded-md border border-destructive/40 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {/* Single audio element: only one stream can ever be active. */}
      <audio
        ref={audioRef}
        preload="none"
        controlsList="nodownload"
        onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onEnded={() => {
          setPlaying(false);
          step(1);
        }}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />

      <section className="mt-8" aria-label="Now playing">
        <div className="rounded-lg border border-border p-4">
          <p className="text-sm text-muted-foreground">
            {active ? `Track ${active.position} of ${state.totalCount}` : "Nothing playing"}
          </p>
          <p className="text-lg font-medium">{active ? active.title : "Select a track"}</p>
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => step(-1)}
              disabled={activeIndex == null}
              aria-label="Previous track"
              className="min-h-11 min-w-11 rounded-md border border-border px-3 disabled:opacity-40"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => (activeIndex == null ? void play(0) : toggle(activeIndex))}
              aria-label={playing ? "Pause" : "Play"}
              className="min-h-11 min-w-11 rounded-md bg-primary px-4 font-medium text-primary-foreground"
            >
              {playing ? "❚❚" : "▶"}
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              disabled={activeIndex == null}
              aria-label="Next track"
              className="min-h-11 min-w-11 rounded-md border border-border px-3 disabled:opacity-40"
            >
              ›
            </button>
            <span className="ml-auto tabular-nums text-sm text-muted-foreground">
              {formatTime(position)} / {formatTime(duration)}
            </span>
          </div>
        </div>
      </section>

      <ol className="mt-8 divide-y divide-border rounded-lg border border-border" aria-label="Album tracklist">
        {tracks.map((track, index) => {
          const isActive = activeIndex === index;
          return (
            <li key={track.slug} className="flex items-center gap-3 px-3 py-2">
              <span className="w-6 shrink-0 tabular-nums text-sm text-muted-foreground">{track.position}</span>
              <button
                type="button"
                onClick={() => toggle(index)}
                disabled={!track.available || loadingIndex === index}
                aria-label={`${isActive && playing ? "Pause" : "Play"} ${track.title}`}
                className="min-h-11 min-w-11 rounded-md border border-border px-3 text-sm disabled:opacity-40"
              >
                {loadingIndex === index ? "…" : isActive && playing ? "❚❚" : "▶"}
              </button>
              <span className={`flex-1 text-sm ${isActive ? "font-medium text-primary" : ""}`}>
                {track.title}
              </span>
              {!track.available ? (
                <span className="text-xs text-muted-foreground">Not loaded</span>
              ) : null}
            </li>
          );
        })}
      </ol>

      <p className="mt-6 text-xs text-muted-foreground">
        Streaming only. {playable} of {state.totalCount} tracks available. Please do not record,
        redistribute or share this link — access is individually issued and can be revoked.
      </p>

      <div className="mt-6">
        <button
          type="button"
          onClick={() => void onExit()}
          className="min-h-11 rounded-md border border-border px-4 text-sm"
        >
          End listening session
        </button>
      </div>

      <PressLinks />
    </main>
  );
}
